import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { makeStyles } from "tss-react/mui";
import { Dialog } from "component/shared/dialog";
import { layoutStyles, linkStyles, shellStyles } from "./style";

// Hooks rather than withStyles: the shell's sheet takes the width and top as parameters, and the
// file exports three components that share nothing but this module.
const useShellStyles = makeStyles<{ width: number; top: number }>()(shellStyles);
const useLayoutStyles = makeStyles()(layoutStyles);
const useLinkStyles = makeStyles()(linkStyles);

interface ShellProps {
	open: boolean;
	onClose: () => void;
	/** Id of the title, which names the dialog. */
	titleId: string;
	/** Figma: 560 for a standard modal, 840 for the setup wizard. */
	width?: number;
	/** Distance from the top of the window. */
	top?: number;
	disableBackdropClick?: boolean;
	"aria-describedby"?: string;
	children?: React.ReactNode;
}

/**
 * The refreshed modal's surface (MAX-4419): top-anchored, radius 8, padding 24. Holds a
 * DialogFrameLayout. Use DialogFrame for both at once; use the two separately when the layout's
 * state has to reset each time the modal opens, since the Dialog unmounts its children on close.
 */
export const DialogFrameShell: React.FC<ShellProps> = ({
	open,
	onClose,
	titleId,
	width = 560,
	top = 80,
	disableBackdropClick,
	children,
	...aria
}) => {
	const { classes } = useShellStyles({ width, top });
	return (
		<Dialog
			open={open}
			onClose={onClose}
			disableBackdropClick={disableBackdropClick}
			maxWidth={false}
			aria-labelledby={titleId}
			aria-describedby={aria["aria-describedby"]}
			// MUI v5 sets role="dialog" on the paper but not aria-modal.
			PaperProps={{ "aria-modal": true }}
			classes={{ container: classes.container, paper: classes.paper }}
		>
			{children}
		</Dialog>
	);
};

interface LayoutProps {
	onClose: () => void;
	title: React.ReactNode;
	titleId: string;
	/** Shown before the title, e.g. a warning icon. */
	titleIcon?: React.ReactNode;
	/** Disables the close X, e.g. while a save runs. */
	closeDisabled?: boolean;
	/** Keeps the title, close button and divider at the top while the content scrolls. */
	stickyHeader?: boolean;
	/** Shown above the buttons, e.g. a failed save. */
	notice?: React.ReactNode;
	/** The button row, right-aligned. Leave it out for a modal with no buttons. */
	actions?: React.ReactNode;
	children?: React.ReactNode;
}

/**
 * Title and close, a divider, the content, and a right-aligned button row, spaced as the Figma
 * does it: 16px between header, divider and content, and 32px above the buttons. Children are laid
 * out 16px apart; group them to space them otherwise.
 */
export const DialogFrameLayout: React.FC<LayoutProps> = ({
	onClose,
	title,
	titleId,
	titleIcon,
	closeDisabled = false,
	stickyHeader = false,
	notice,
	actions,
	children,
}) => {
	const { classes } = useLayoutStyles();
	const header = (
		<>
			{/* Its own id: DialogTitle otherwise takes the Dialog's aria-labelledby id, which the h2 carries. */}
			<Mui.DialogTitle id={`${titleId}-header`} className={classes.header} component="div">
				<span className={classes.titleRow}>
					{titleIcon}
					<Mui.Typography id={titleId} variant="inherit" component="h2">
						{title}
					</Mui.Typography>
				</span>
				<Mui.IconButton className={classes.close} onClick={onClose} disabled={closeDisabled} aria-label="Close">
					<Icons.X size={24} />
				</Mui.IconButton>
			</Mui.DialogTitle>
			<Mui.Divider className={classes.divider} />
		</>
	);
	return (
		<>
			<div className={classes.body}>
				{stickyHeader ? <div className={classes.stickyHeader}>{header}</div> : header}
				{children}
			</div>
			{(notice || actions) && (
				<div className={classes.footer}>
					{notice}
					{actions && <div className={classes.actions}>{actions}</div>}
				</div>
			)}
		</>
	);
};

type Props = Omit<ShellProps, "children"> & LayoutProps;

/**
 * A refreshed modal: DialogFrameShell holding a DialogFrameLayout.
 *
 * Why this exists next to SettingsDialog (the notifications refresh): SettingsDialog is a fixed
 * two-button confirm, with Cancel always rendered, a single confirm action and no scroll handling.
 * The setup refresh needed what that shape can't hold: any button row, or none; a notice above the
 * buttons; a title icon; a 840 wizard width; top anchoring; a body that scrolls without clipping;
 * and a shell and layout that can be split so a wizard's state resets on open. Merging the two
 * into one shell is a follow-up (FD-VPLAT-24 audit #6); until then, new refreshed modals use this.
 */
export const DialogFrame: React.FC<Props> = ({
	open,
	width,
	top,
	disableBackdropClick,
	"aria-describedby": describedBy,
	...layout
}) => (
	<DialogFrameShell
		open={open}
		onClose={layout.onClose}
		titleId={layout.titleId}
		width={width}
		top={top}
		disableBackdropClick={disableBackdropClick}
		aria-describedby={describedBy}
	>
		<DialogFrameLayout {...layout} />
	</DialogFrameShell>
);

/** A link inside modal copy, underlined as the Figma shows it. Colour comes from `className`. */
export const DialogLink: React.FC<Mui.LinkProps> = ({ className, ...props }) => {
	const { classes, cx } = useLinkStyles();
	return <Mui.Link {...props} className={cx(classes.link, className)} />;
};
